<template>
  <v-dialog
    v-model="dialog"
    :fullscreen="$vuetify.breakpoint.mobile"
    scrollable
    persistent
    max-width="950px"
    transition="dialog-transition"
  >
    <v-card>
      <v-card-title class="header">
        <v-icon left>mdi-truck-edit</v-icon>
        <span class="header-text">
          Edição de Entrega Nº {{ delivery.number }}
        </span>
        <v-spacer />
        <v-btn icon color="" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="content pa-4">
        <v-row dense>
          <v-col cols="12" lg="12">
            <v-card outlined class="section mb-3">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">mdi-cart</v-icon>
                Informações da Venda
              </v-card-subtitle>
              <v-card-text class="pt-0">
                <v-row dense>
                  <v-col cols="6">
                    <div class="info-item">
                      <span class="label">Venda Nº</span>
                      <span class="value primary--text">
                        {{ delivery.sale.number }}
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="info-item">
                      <span class="label">Cliente</span>
                      <span class="value">
                        {{
                          delivery.sale.customer
                            ? delivery.sale.customer.name
                            : "Consumidor final"
                        }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card outlined class="section mb-3">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">
                  mdi-office-building
                </v-icon>
                Empresa Responsável
              </v-card-subtitle>
              <v-card-text class="pt-4">
                <company-select
                  chips
                  v-model="delivery.company_id"
                  :item-value="(val) => val.id"
                  :itemText="(val) => val.name"
                  style="width: 100%"
                />
              </v-card-text>
            </v-card>

            <v-card outlined class="section mb-3">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">
                  mdi-map-marker
                </v-icon>
                Endereço de Entrega
              </v-card-subtitle>
              <v-card-text class="py-3">
                <v-row dense>
                  <v-col cols="8">
                    <v-text-field
                      v-model="delivery.main_address.address"
                      label="Rua / Logradouro"
                      outlined
                      dense
                      hide-details
                      class="field"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="delivery.main_address.number"
                      label="Número"
                      outlined
                      dense
                      hide-details
                      class="field"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="delivery.main_address.district"
                      label="Bairro"
                      outlined
                      dense
                      hide-details
                      class="field"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="delivery.main_address.complement"
                      label="Complemento"
                      outlined
                      dense
                      hide-details
                      class="field"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="delivery.main_address.city"
                      label="Cidade"
                      outlined
                      dense
                      hide-details
                      class="field"
                    />
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      v-model="delivery.main_address.state"
                      label="UF"
                      outlined
                      dense
                      maxlength="2"
                      hide-details
                      class="field"
                    />
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      v-model="delivery.main_address.postal_code"
                      label="CEP"
                      outlined
                      dense
                      placeholder="00000-000"
                      hide-details
                      class="field"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card outlined class="section mb-3">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">
                  mdi-note-text
                </v-icon>
                Observações
                <span
                  class="caption ml-2 text--secondary"
                  style="text-transform: none;"
                >
                  (Descreva o motivo da edição desta entrega)
                </span>
              </v-card-subtitle>
              <v-card-text class="py-3">
                <v-row dense>
                  <v-col cols="12">
                    <v-textarea
                      v-model="delivery.comment"
                      label="Observações sobre a edição"
                      outlined
                      dense
                      rows="3"
                      hide-details
                      class="field"
                      placeholder="Ex: Cliente solicitou alteração no endereço, produto danificado, etc."
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Produtos da Entrega - Largura Total -->
        <v-row dense class="mb-3">
          <v-col cols="12">
            <v-card outlined class="section">
              <v-card-subtitle class="section-header pb-2">
                <v-icon small color="primary" class="mr-2">
                  mdi-package-variant
                </v-icon>
                Produtos da Entrega
              </v-card-subtitle>
              <v-card-text class="py-3">
                <v-data-table
                  :headers="productHeaders"
                  :items="editableProducts"
                  :items-per-page="-1"
                  hide-default-footer
                  disable-sort
                >
                  <template v-slot:[`item.product.code`]="{ item }">
                    {{ item.product ? item.product.code : "" }}
                  </template>
                  <template v-slot:[`item.product.name`]="{ item }">
                    <div
                      class="product-description"
                      :title="item.product ? item.product.name : ''"
                    >
                      {{ item.product ? item.product.name : "" }}
                    </div>
                  </template>
                  <template v-slot:[`item.current_quantity`]="{ item }">
                    {{ $format.decimal(item.current_quantity) }}
                  </template>
                  <template v-slot:[`item.available`]="{ item }">
                    {{ $format.decimal(item.available_quantity) }}
                  </template>
                  <template v-slot:[`item.new_quantity`]="{ item }">
                    <app-number-field
                      v-model="item.new_quantity"
                      @input="validateForm"
                    />
                  </template>
                  <template v-slot:[`item.actions`]="{ index, item }">
                    <v-btn @click="setMaxQuantity(index, item)" icon>
                      <v-icon>mdi-package-variant-closed-plus</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>

                <div class="d-flex justify-end mt-4">
                  <v-btn
                    @click="setAllMaxQuantity()"
                    class="text-capitalize"
                    text
                    color="info"
                  >
                    Entregar tudo
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Log de Edição -->
        <v-row v-if="delivery.edited_at" dense class="mb-3">
          <v-col cols="12">
            <v-alert type="info" dense outlined class="text-body-2 mb-0">
              <v-icon small left>mdi-information</v-icon>
              <strong>Última edição:</strong>
              {{ $format.dateBr(delivery.edited_at) }}
              por {{ delivery.editedBy ? delivery.editedBy.name : "Sistema" }}
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="actions">
        <v-spacer />
        <v-btn text @click="close" class="text-body-2">
          <v-icon left small>mdi-close</v-icon>
          Fechar
        </v-btn>

        <v-btn
          color="primary"
          :disabled="!isFormValid || loading"
          :loading="loading"
          @click="saveChanges"
          class="ml-3 text-body-2"
        >
          <v-icon left small>mdi-content-save</v-icon>
          Salvar Alterações
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CompanySelect from "@/components/company/ui/CompanySelect.vue";

export default {
  components: { CompanySelect },

  data() {
    return {
      dialog: false,
      deliveryId: null,
      loading: false,
      isFormValid: true,
      delivery: {
        sale: {
          customer: {},
        },
        delivery_products: [],
        company_id: null,
        main_address: {},
        comment: "",
      },
      editableProducts: [],
      productHeaders: [
        {
          text: "Código",
          value: "product.code",
          align: "center",
          width: "5%",
        },
        { text: "Descrição", value: "product.name", width: "75%" },
        {
          text: "Atual",
          value: "current_quantity",
          align: "right",
          width: "5%",
        },
        {
          text: "Disponível",
          value: "available",
          align: "right",
          width: "5%",
        },
        {
          text: "Entregar",
          value: "new_quantity",
          align: "right",
          width: "10%",
        },
        { text: "", value: "actions", align: "right", width: "5%" },
      ],
    };
  },

  computed: {},

  methods: {
    // Dialog Management
    async open(deliveryId) {
      this.deliveryId = deliveryId;
      await this.loadDelivery();
      this.dialog = true;
    },

    close() {
      this.dialog = false;
      this.deliveryId = null;
      this.resetData();
    },

    resetData() {
      this.delivery = {
        sale: {
          customer: {},
        },
        delivery_products: [],
        main_address: {},
        comment: "",
        company_id: null,
      };
      this.editableProducts = [];
    },

    // Data Loading
    async loadDelivery() {
      this.$loading.start();

      try {
        const response = await this.$http.show(
          "sale/delivery-update",
          this.deliveryId
        );

        this.delivery = response.delivery;
        this.prepareEditableProducts();
        this.ensureMainAddress();
        this.validateForm();
      } catch (error) {
        console.error("Erro ao carregar entrega:", error);
      } finally {
        this.$loading.finish();
      }
    },

    prepareEditableProducts() {
      this.editableProducts = this.delivery.editable_products.map((item) => ({
        ...item,
        new_quantity: parseFloat(item.current_quantity) || 0,
        available_quantity: parseFloat(item.available_quantity) || 0,
        current_quantity: parseFloat(item.current_quantity) || 0,
      }));
    },

    ensureMainAddress() {
      if (!this.delivery.main_address) {
        this.delivery.main_address = {
          address: "",
          number: "",
          district: "",
          complement: "",
          city: "",
          state: "",
          postal_code: "",
        };
      }
    },

    // Validation
    validateQuantity(item) {
      return (value) => {
        const max = parseFloat(item.available_quantity) || 0;
        const val = parseFloat(value) || 0;
        if (val < 0) return "Quantidade não pode ser menor que 0";
        if (val > max) return `Quantidade máxima disponível: ${max}`;
        return true;
      };
    },

    validateForm() {
      const hasValidProduct = this.editableProducts.some(
        (item) => parseFloat(item.new_quantity) > 0
      );

      const allQuantitiesValid = this.editableProducts.every((item) => {
        const max = parseFloat(item.available_quantity) || 0;
        const val = parseFloat(item.new_quantity) || 0;
        return val >= 0 && val <= max;
      });

      this.isFormValid = hasValidProduct && allQuantitiesValid;
    },

    // Product Quantity Management
    setMaxQuantity(index, item) {
      item.new_quantity = parseFloat(item.available_quantity) || 0;
      this.$set(this.editableProducts, index, item);
      this.validateForm();
    },

    setAllMaxQuantity() {
      this.editableProducts.forEach((item) => {
        item.new_quantity = parseFloat(item.available_quantity) || 0;
      });
      this.validateForm();
    },

    // Actions
    async saveChanges() {
      if (!this.isFormValid) {
        alert("Verifique os dados antes de salvar");
        return;
      }

      this.loading = true;

      try {
        const payload = this.buildSavePayload();

        await this.$http.update(
          "sale/delivery-update",
          this.deliveryId,
          payload
        );

        this.$emit("update");
        this.close();
      } catch (err) {
        console.error("Erro ao salvar alterações:", err);
      } finally {
        this.loading = false;
      }
    },

    buildSavePayload() {
      return {
        delivery_products: this.editableProducts
          .filter((item) => item.new_quantity > 0)
          .map((item) => ({
            delivery_product_id: item.delivery_product_id,
            sale_product_id: item.sale_product_id,
            quantity: item.new_quantity,
            is_in_delivery: item.is_in_delivery,
          })),
        comment: this.delivery.comment,
        company_id: this.delivery.company_id,
        address: {
          ...this.delivery.main_address,
        },
      };
    },
  },
};
</script>

<style scoped>
.section-header {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #f5f5f5;
  margin: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
}

/* Informações compactas */
.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.label {
  font-size: 0.7rem;
  font-weight: 500;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.value {
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
}

.product-description {
  font-size: 0.8rem;
}

/* Campos de formulário ERP */
.field >>> .v-input__control {
  min-height: 32px;
}

.field >>> .v-input__slot {
  min-height: 32px;
  font-size: 0.8rem;
}

.field >>> .v-text-field__details {
  display: none;
}

.field >>> .v-label {
  font-size: 0.8rem;
}

.actions {
  flex-shrink: 0;
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.header {
  flex-shrink: 0;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
}
</style>
